import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { clsx } from 'clsx'
import { SUPPORTED_LANGUAGES } from '@/i18n/config'
import { usePlatform } from '@/hooks'

export default function LanguageSwitcher() {
  const { i18n, t } = useTranslation()
  const platform = usePlatform()
  const isPC = platform === 'pc'
  const [open, setOpen] = useState(false)

  const resolvedLang = i18n.resolvedLanguage || i18n.language
  const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === resolvedLang) || SUPPORTED_LANGUAGES[0]

  const handleChange = (code: string) => {
    i18n.changeLanguage(code)
    setOpen(false)
  }

  /* ---------- APP: bottom sheet ---------- */
  if (!isPC) {
    return (
      <>
        <button
          onClick={() => setOpen(true)}
          className="flex items-center px-2 py-1.5 rounded-lg hover:bg-neutral-100 transition"
        >
          <span className="text-lg">{currentLang?.flag}</span>
          <span className="material-icons-round text-neutral-400 text-lg">expand_more</span>
        </button>

        {open && createPortal(
          <div
            className="fixed inset-0 bg-black/50 z-modal"
            onClick={() => setOpen(false)}
          >
            <div
              className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-6 pb-20 animate-slide-up-drawer"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-lg font-semibold text-neutral-900">
                  {t('lang.switchLang')}
                </span>
                <button
                  onClick={() => setOpen(false)}
                  className="p-2 hover:bg-neutral-100 rounded-lg"
                >
                  <span className="material-icons-round text-neutral-600">close</span>
                </button>
              </div>
              <div className="space-y-2">
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleChange(lang.code)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl hover:bg-blue-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{lang.flag}</span>
                      <span className="text-base font-medium text-neutral-700">{lang.name}</span>
                    </div>
                    {resolvedLang === lang.code && (
                      <span className="material-icons-round text-blue-600">check</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>,
          document.body,
        )}
      </>
    )
  }

  /* ---------- PC: full-width top panel ---------- */
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className={clsx(
          'flex items-center space-x-2 px-3 py-2 rounded-lg transition',
          open ? 'bg-blue-50 text-blue-600' : 'hover:bg-neutral-100'
        )}
      >
        <span className="material-icons-round text-xl">language</span>
        <span className="text-sm font-medium text-neutral-700 hidden sm:inline">
          {currentLang?.flag} {currentLang?.name}
        </span>
        <span
          className={clsx(
            'material-icons-round text-neutral-400 text-lg transition-transform',
            open && 'rotate-180'
          )}
        >
          expand_more
        </span>
      </button>

      {open && createPortal(
        <div className="fixed inset-0 top-[80px] z-modal" onClick={() => setOpen(false)}>
          {/* backdrop */}
          <div className="absolute inset-0 bg-black/20" />

          {/* panel */}
          <div
            className="relative bg-white border-b border-neutral-200 shadow-lg animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-w-7xl mx-auto px-6 py-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-neutral-500 uppercase tracking-wide">
                  {t('lang.switchLang')}
                </span>
                <button
                  onClick={() => setOpen(false)}
                  className="p-1 hover:bg-neutral-100 rounded-md transition"
                >
                  <span className="material-icons-round text-neutral-400 text-xl">close</span>
                </button>
              </div>
              <div className="flex flex-wrap gap-3">
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleChange(lang.code)}
                    className={clsx(
                      'flex items-center space-x-2.5 px-5 py-2.5 rounded-xl border transition-all',
                      resolvedLang === lang.code
                        ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm'
                        : 'border-neutral-200 text-neutral-700 hover:bg-neutral-50 hover:border-neutral-300'
                    )}
                  >
                    <span className="text-xl">{lang.flag}</span>
                    <span className="text-sm font-medium">{lang.name}</span>
                    {resolvedLang === lang.code && (
                      <span className="material-icons-round text-blue-600 text-lg">check_circle</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
